import api from '@/api'

export interface NotificationItem {
  id: string | number
  title: string
  content: string
  type?: string
  level?: number
  createdAt?: string
  read?: boolean
  data?: any
}

interface State {
  list: NotificationItem[]
  unreadCount: number
  currentPage: number
  pageSize: number
  total: number
  loading: boolean
  hasMore: boolean
  listRequestSeq: number
}

export const useNotificationStore = defineStore('notification', {
  state: (): State => ({
    list: [],
    unreadCount: 0,
    currentPage: 1,
    pageSize: 10,
    total: 0,
    loading: false,
    hasMore: true,
    listRequestSeq: 0,
  }),

  actions: {
    extractSocketPayload(raw: any) {
      let payload = raw

      if (raw && typeof raw === 'object' && 'data' in raw) {
        if (typeof raw.data === 'string') {
          try {
            payload = JSON.parse(raw.data)
          }
          catch {
            payload = raw
          }
        }
        else if (raw.data && typeof raw.data === 'object') {
          payload = raw.data
        }
      }

      return payload
    },

    normalizeNotification(raw: any): NotificationItem {
      const payload = this.extractSocketPayload(raw)

      return {
        id: payload.id ?? payload.notification_id ?? raw?.id ?? Date.now(),
        title: payload.title ?? raw?.title ?? '系统通知',
        content: payload.content ?? payload.message ?? raw?.content ?? raw?.message ?? '',
        type: String(payload.type ?? raw?.type ?? ''),
        level: Number(payload.level ?? raw?.level ?? 1),
        createdAt: payload.created_at ?? payload.time ?? raw?.created_at ?? raw?.time ?? new Date().toISOString(),
        read: payload.is_read === 1 || payload.read === true || raw?.is_read === 1 || raw?.read === true,
        data: {
          ...payload,
          channel: payload.channel ?? raw?.channel,
          event: payload.event ?? raw?.event,
        },
      }
    },

    mergeNotifications(items: NotificationItem[]) {
      const map = new Map<string | number, NotificationItem>()
      const merged = [...items, ...this.list]

      for (const item of merged) {
        map.set(item.id, item)
      }

      this.list = Array.from(map.values()).sort((a, b) => {
        const aTime = new Date(a.createdAt || 0).getTime()
        const bTime = new Date(b.createdAt || 0).getTime()
        return bTime - aTime
      })
    },

    async fetchNotificationList(payload?: {
      channel?: string | number
      unread_only?: string | number
      page?: number
      pageSize?: number
      append?: boolean
    }) {
      const channel = payload?.channel
      const page = payload?.page ?? 1
      const pageSize = payload?.pageSize ?? this.pageSize
      const append = payload?.append ?? false
      const unread_only = payload?.unread_only ?? '-1'
      const requestSeq = this.listRequestSeq + 1
      this.listRequestSeq = requestSeq

      this.loading = true
      try {
        const params: Record<string, string> = {
          page_size: String(pageSize),
          page: String(page),
          unread_only: String(unread_only),
        }

        if (channel !== undefined && channel !== null && channel !== '') {
          params.channel = String(channel)
        }

        const res = await api.get('/common/notifications/list', {
          params,
        })

        const rows = (res?.data?.data ?? []).map((item: any) => this.normalizeNotification(item))

        if (requestSeq !== this.listRequestSeq) {
          return
        }

        this.currentPage = Number(res?.data?.current_page ?? page)
        this.pageSize = Number(res?.data?.per_page ?? pageSize)
        this.total = Number(res?.data?.total ?? rows.length)
        this.hasMore = this.currentPage * this.pageSize < this.total

        if (append) {
          this.mergeNotifications(rows)
        }
        else {
          this.list = rows
        }
      }
      finally {
        if (requestSeq === this.listRequestSeq) {
          this.loading = false
        }
      }
    },

    async fetchUnreadCount() {
      const res = await api.get('/common/notifications/unread-count')
      this.unreadCount = Number(res?.data?.unread_count ?? 0)
    },

    async fetchNextPage(payload?: { channel?: string | number, unread_only?: string | number }) {
      if (this.loading || !this.hasMore) {
        return
      }
      await this.fetchNotificationList({
        channel: payload?.channel,
        unread_only: payload?.unread_only,
        page: this.currentPage + 1,
        pageSize: this.pageSize,
        append: true,
      })
    },

    pushNotification(notification: NotificationItem) {
      const normalized = this.normalizeNotification(notification)
      const exists = this.list.find(item => item.id === normalized.id)
      const wasUnread = exists ? !exists.read : false

      if (!exists) {
        this.list.unshift(normalized)
      }
      else {
        Object.assign(exists, normalized)
      }

      if (!normalized.read && (!exists || !wasUnread)) {
        this.unreadCount += 1
      }
    },

    markAllRead() {
      this.list = this.list.map(item => ({
        ...item,
        read: true,
      }))
      this.unreadCount = 0
    },

    async readAllNotifications() {
      await api.put('/common/notifications/read-all')
      this.markAllRead()
    },

    markOneRead(id: string | number) {
      const target = this.list.find(item => item.id === id)
      if (target && !target.read) {
        target.read = true
        this.unreadCount = Math.max(0, this.unreadCount - 1)
      }
    },

    async readOneNotification(id: string | number) {
      await api.put(`/common/notifications/read/${id}`)
      this.markOneRead(id)
    },

    reset() {
      this.list = []
      this.unreadCount = 0
      this.currentPage = 1
      this.pageSize = 10
      this.total = 0
      this.loading = false
      this.hasMore = true
      this.listRequestSeq = 0
    },
  },
})
